import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BlogTab from "@/pages/admin/BlogTab";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Users,
  CreditCard,
  Wallet,
  TrendingUp,
  Ticket,
  Sparkles,
  Plus,
  Check,
  X,
  Trash2,
  Power,
  Loader2,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };

export default function Admin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "stats";
  const [tab, setTab] = useState(initialTab);

  const handleTabChange = (v) => {
    setTab(v);
    // Preserve deep-link params (e.g. ?post_id=xxx) when switching to blog
    if (v === "blog") {
      const preserved = new URLSearchParams();
      preserved.set("tab", "blog");
      const pid = searchParams.get("post_id");
      if (pid) preserved.set("post_id", pid);
      setSearchParams(preserved, { replace: true });
    } else {
      setSearchParams({ tab: v }, { replace: true });
    }
  };

  if (user && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="admin-page">
      <header className="mb-8">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#9ACD32]/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#9ACD32]">
          <Sparkles className="h-3 w-3" />
          ADMIN
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
          {t("admin.title")}
        </h1>
        <p className="mt-2 text-slate-300">{t("admin.subtitle")}</p>
      </header>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-slate-900">
          <TabsTrigger value="stats" data-testid="admin-tab-stats">{t("admin.tab_stats")}</TabsTrigger>
          <TabsTrigger value="sources" data-testid="admin-tab-sources">{t("admin.tab_sources")}</TabsTrigger>
          <TabsTrigger value="promos" data-testid="admin-tab-promos">{t("admin.tab_promos")}</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="admin-tab-affiliates">{t("admin.tab_affiliates")}</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="admin-tab-commissions">{t("admin.tab_commissions")}</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">{t("admin.tab_blog")}</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-6">
          <StatsTab />
        </TabsContent>
        <TabsContent value="sources" className="mt-6">
          <SourcesTab />
        </TabsContent>
        <TabsContent value="promos" className="mt-6">
          <PromosTab />
        </TabsContent>
        <TabsContent value="affiliates" className="mt-6">
          <AffiliatesTab />
        </TabsContent>
        <TabsContent value="commissions" className="mt-6">
          <CommissionsTab />
        </TabsContent>
        <TabsContent value="blog" className="mt-6">
          <BlogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ STATS TAB ============
function StatsTab() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useImperativeLoad(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-[#9ACD32]" />;

  return (
    <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-4">
      <KPI icon={Users} label={t("admin.stat_users")} value={s.total_users} testId="kpi-users" />
      <KPI icon={CreditCard} label={t("admin.stat_paid")} value={s.paid_users} testId="kpi-paid" />
      <KPI icon={TrendingUp} label={t("admin.stat_mrr")} value={`€${s.mrr_eur.toFixed(2)}`} accent testId="kpi-mrr" />
      <KPI icon={Wallet} label={t("admin.stat_revenue")} value={`€${s.total_revenue_eur.toFixed(2)}`} testId="kpi-revenue" />
      <KPI icon={Users} label={t("admin.stat_affiliates")} value={`${s.affiliates_approved} (${s.affiliates_pending} pending)`} testId="kpi-affiliates" />
      <KPI icon={Wallet} label={t("admin.stat_commissions")} value={`€${s.commissions_pending_eur.toFixed(2)}`} testId="kpi-commissions" />
      <Card className="border-slate-800 bg-[#0A0C1F] p-5 md:col-span-3 lg:col-span-2" data-testid="kpi-tiers">
          <p className="text-xs uppercase tracking-wider text-slate-500">{t("admin.tiers_breakdown")}</p>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {Object.entries(s.tier_breakdown).map(([k, v]) => (
            <div key={k} className="text-center">
              <p className="text-xs uppercase text-slate-500">{TIER_LABEL[k]}</p>
              <p className="font-display text-2xl font-extrabold text-white">{v}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className={`border-slate-800 bg-[#0A0C1F] p-5 ${accent ? "ring-1 ring-[#9ACD32]/30" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#9ACD32]" : "text-slate-500"}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-extrabold text-white">{value}</div>
    </Card>
  );
}

// ============ SOURCES TAB (Acquisition analytics) ============
const SOURCE_LABELS = {
  qr: "QR landing",
  install_banner: "Install banner",
  install_ios: "iOS hint",
  share: "Web share",
  shortcut: "Shortcut",
  partner: "Partner / Affiliate",
  referral: "Referral",
  social: "Social",
  email: "Email",
  ads: "Ads",
  search: "Search",
  direct: "Direct",
  other: "Other",
};

function SourcesTab() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useImperativeLoad(() => {
    api.get("/admin/analytics/sources")
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], totals: { signups: 0, paid_signups: 0, installs: 0 } }));
  }, []);

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-[#9ACD32]" />;

  const totals = data.totals || { signups: 0, paid_signups: 0, installs: 0 };
  // Only show rows that have at least one signal — keeps the table focused
  const rows = (data.rows || []).filter(
    (r) => r.signups > 0 || r.installs > 0 || r.paid_signups > 0
  );

  return (
    <div data-testid="admin-sources-tab">
      {/* Totals */}
      <div className="mb-6 grid gap-5 md:grid-cols-3">
        <KPI
          icon={Users}
          label={t("admin.sources_total_signups")}
          value={totals.signups}
          testId="kpi-sources-signups"
        />
        <KPI
          icon={CreditCard}
          label={t("admin.sources_total_paid")}
          value={totals.paid_signups}
          accent
          testId="kpi-sources-paid"
        />
        <KPI
          icon={Download}
          label={t("admin.sources_total_installs")}
          value={totals.installs}
          testId="kpi-sources-installs"
        />
      </div>

      {/* Per-source table */}
      <Card className="border-slate-800 bg-[#0A0C1F] p-0 overflow-hidden">
        <div className="border-b border-slate-800 p-5">
          <h3 className="font-display text-lg font-bold text-white">
            {t("admin.sources_table_title")}
          </h3>
          <p className="mt-1 text-xs text-slate-400">
            {t("admin.sources_table_desc")}
          </p>
        </div>

        {rows.length === 0 ? (
          <div
            className="p-8 text-center text-sm text-slate-500"
            data-testid="admin-sources-empty"
          >
            {t("admin.sources_empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-sources-table">
              <thead className="bg-slate-900/60">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">{t("admin.sources_col_source")}</th>
                  <th className="px-5 py-3 text-right">{t("admin.sources_col_signups")}</th>
                  <th className="px-5 py-3 text-right">{t("admin.sources_col_paid")}</th>
                  <th className="px-5 py-3 text-right">{t("admin.sources_col_installs")}</th>
                  <th className="px-5 py-3 text-right">{t("admin.sources_col_conv")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => {
                  const convRate =
                    r.signups > 0 ? (r.paid_signups / r.signups) * 100 : 0;
                  const isHero = r.source === "qr";
                  return (
                    <tr
                      key={r.source}
                      className={isHero ? "bg-[#9ACD32]/5" : ""}
                      data-testid={`admin-sources-row-${r.source}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {isHero && (
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#9ACD32]" />
                          )}
                          <span className="font-medium text-white">
                            {SOURCE_LABELS[r.source] || r.source}
                          </span>
                          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                            {r.source}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-200">
                        {r.signups}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-[#9ACD32]">
                        {r.paid_signups}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-200">
                        {r.installs}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-400">
                        {convRate.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="mt-3 text-xs text-slate-500">
        {t("admin.sources_legend")}
      </p>
    </div>
  );
}

// ============ PROMOS TAB ============
function PromosTab() {
  const { t } = useI18n();
  const [promos, setPromos] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: "",
    plan_id: "pro",
    discounted_price_eur: "",
    max_uses: "",
    description: "",
  });

  const load = async () => {
    const r = await api.get("/admin/promos");
    setPromos(r.data.promos || []);
  };
  useImperativeLoad(() => { load(); }, []);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        plan_id: form.plan_id,
        discounted_price_eur: parseFloat(form.discounted_price_eur),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        description: form.description,
      };
      await api.post("/admin/promos", body);
      toast.success(t("admin.promo_created"));
      setShowCreate(false);
      setForm({ code: "", plan_id: "pro", discounted_price_eur: "", max_uses: "", description: "" });
      load();
    } catch (err) {
      toast.error(`Error: ${extractErrorCode(err)}`);
    }
  };

  const toggleActive = async (p) => {
    await api.patch(`/admin/promos/${p.promo_id}`, { active: !p.active });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete promo?")) return;
    await api.delete(`/admin/promos/${id}`);
    load();
  };

  return (
    <div data-testid="admin-promos-tab">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-white">{t("admin.promos_title")}</h2>
        <Button
          onClick={() => setShowCreate(true)}
          className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
          data-testid="admin-new-promo-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("admin.new_promo")}
        </Button>
      </div>

      <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3">{t("admin.table_code")}</th>
              <th className="px-5 py-3">{t("admin.table_plan")}</th>
              <th className="px-5 py-3">{t("admin.table_discount")}</th>
              <th className="px-5 py-3">{t("admin.table_max")}</th>
              <th className="px-5 py-3">{t("admin.table_used")}</th>
              <th className="px-5 py-3">{t("admin.table_active")}</th>
              <th className="px-5 py-3">{t("admin.table_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {promos.map((p) => (
              <tr key={p.promo_id} className="border-b border-slate-800/60" data-testid={`promo-row-${p.code}`}>
                <td className="px-5 py-3 font-mono font-bold text-[#9ACD32]">{p.code}</td>
                <td className="px-5 py-3 text-white">{TIER_LABEL[p.plan_id]}</td>
                <td className="px-5 py-3 font-mono text-slate-300">€{p.discounted_price_eur}</td>
                <td className="px-5 py-3 text-slate-400">{p.max_uses ?? "∞"}</td>
                <td className="px-5 py-3 text-slate-400">{p.used_count}</td>
                <td className="px-5 py-3">
                  <span className={`tier-badge ${p.active ? "tier-pro" : "tier-free"}`}>
                    {p.active ? "ON" : "OFF"}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleActive(p)}
                      className="h-7 rounded-full border-slate-700 text-xs"
                      data-testid={`promo-toggle-${p.code}`}
                    >
                      <Power className="mr-1 h-3 w-3" />
                      {p.active ? t("admin.deactivate") : t("admin.activate")}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(p.promo_id)}
                      className="h-7 rounded-full text-xs"
                      data-testid={`promo-delete-${p.code}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {promos.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-sm text-slate-500">
                  {t("admin.no_data")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowCreate(false)}
        >
          <Card
            className="w-full max-w-md border-slate-800 bg-[#0A0C1F] p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-promo-modal"
          >
            <h3 className="font-display text-xl font-bold text-white">{t("admin.create_promo_title")}</h3>
            <form onSubmit={handleCreate} className="mt-5 space-y-3">
              <div>
                <Label className="text-xs text-slate-400">{t("admin.f_code")}</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="mt-1 h-10 font-mono uppercase"
                  maxLength={20}
                  required
                  data-testid="admin-promo-code-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("admin.f_plan")}</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger className="mt-1 h-10" data-testid="admin-promo-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter (€29)</SelectItem>
                    <SelectItem value="pro">Pro (€79)</SelectItem>
                    <SelectItem value="business">Business (€199)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("admin.f_discount")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.discounted_price_eur}
                  onChange={(e) => setForm({ ...form, discounted_price_eur: e.target.value })}
                  className="mt-1 h-10"
                  required
                  data-testid="admin-promo-discount-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("admin.f_max_uses")}</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="mt-1 h-10"
                  data-testid="admin-promo-maxuses-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("admin.f_description")}</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1"
                  data-testid="admin-promo-desc-input"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid="admin-promo-submit-btn">
                  {t("admin.create")}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============ AFFILIATES TAB ============
function AffiliatesTab() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/affiliates");
    setList(r.data.affiliates || []);
  };
  useImperativeLoad(() => { load(); }, []);

  const approve = async (user_id) => {
    await api.post(`/admin/affiliates/${user_id}/approve`, { commission_rate: 0.20 });
    toast.success(t("admin.affiliate_approved"));
    load();
  };
  const reject = async (user_id) => {
    await api.post(`/admin/affiliates/${user_id}/reject`);
    toast.success(t("admin.affiliate_rejected"));
    load();
  };

  const pending = list.filter((a) => a.affiliate_status === "pending");
  const approved = list.filter((a) => a.affiliate_status === "approved");

  return (
    <div data-testid="admin-affiliates-tab">
      <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{t("admin.af_pending")} ({pending.length})</h2>
      {pending.length === 0 ? (
        <p className="text-sm text-slate-500">{t("admin.no_data")}</p>
      ) : (
        <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
          <table className="w-full text-sm">
            <tbody>
              {pending.map((a) => (
                <tr key={a.user_id} className="border-b border-slate-800/60" data-testid={`affiliate-pending-${a.user_id}`}>
                  <td className="px-5 py-4">
                    <p className="font-semibold text-white">{a.name || a.email}</p>
                    <p className="text-xs text-slate-500">{a.email}</p>
                    {a.affiliate_application && (
                      <p className="mt-2 text-xs text-slate-400">
                        {a.affiliate_application.company_name && <>{t("admin.app_company")}: {a.affiliate_application.company_name} · </>}
                        {a.affiliate_application.website && <>{t("admin.app_web")}: {a.affiliate_application.website} · </>}
                        {a.affiliate_application.expected_monthly_referrals && <>{t("admin.app_volume")}: {a.affiliate_application.expected_monthly_referrals}/{t("admin.app_per_month")}</>}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => approve(a.user_id)} className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid={`approve-affiliate-${a.user_id}`}>
                        <Check className="mr-1 h-3 w-3" />
                        {t("admin.approve")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(a.user_id)} className="rounded-full border-red-500/50 text-red-300" data-testid={`reject-affiliate-${a.user_id}`}>
                        <X className="mr-1 h-3 w-3" />
                        {t("admin.reject")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <h2 className="mb-4 mt-10 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{t("admin.af_approved")} ({approved.length})</h2>
      {approved.length === 0 ? (
        <p className="text-sm text-slate-500">{t("admin.no_data")}</p>
      ) : (
        <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
          <table className="w-full text-sm">
            <tbody>
              {approved.map((a) => (
                <tr key={a.user_id} className="border-b border-slate-800/60">
                  <td className="px-5 py-3">
                    <p className="font-semibold text-white">{a.name || a.email}</p>
                    <p className="text-xs text-slate-500">{a.email}</p>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[#9ACD32]">
                    {Math.round((a.affiliate_commission_rate || 0.2) * 100)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ============ COMMISSIONS TAB ============
function CommissionsTab() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/commissions");
    setList(r.data.commissions || []);
  };
  useImperativeLoad(() => { load(); }, []);
  const markPaid = async (id) => {
    await api.post(`/admin/commissions/${id}/mark_paid`);
    toast.success(t("admin.marked_paid"));
    load();
  };

  return (
    <div data-testid="admin-commissions-tab">
      {list.length === 0 ? (
        <p className="text-sm text-slate-500">{t("admin.no_data")}</p>
      ) : (
        <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">{t("admin.col_date")}</th>
                <th className="px-5 py-3">{t("admin.col_affiliate")}</th>
                <th className="px-5 py-3">{t("admin.col_referred")}</th>
                <th className="px-5 py-3">{t("admin.col_plan")}</th>
                <th className="px-5 py-3">{t("admin.col_commission")}</th>
                <th className="px-5 py-3">{t("admin.col_status")}</th>
                <th className="px-5 py-3">{t("admin.col_action")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.commission_id} className="border-b border-slate-800/60" data-testid={`admin-commission-row-${c.commission_id}`}>
                  <td className="px-5 py-3 text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 font-mono text-slate-300">{c.affiliate_user_id.slice(0, 12)}</td>
                  <td className="px-5 py-3 font-mono text-slate-400">{c.referred_email_masked}</td>
                  <td className="px-5 py-3 text-white">{TIER_LABEL[c.plan_id]}</td>
                  <td className="px-5 py-3 font-mono font-bold text-[#9ACD32]">€{c.commission_amount.toFixed(2)}</td>
                  <td className="px-5 py-3">
                    <span className={`tier-badge tier-${c.status === "paid" ? "business" : c.status === "ready_for_payout" ? "pro" : "free"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {c.status !== "paid" && (
                      <Button size="sm" onClick={() => markPaid(c.commission_id)} className="rounded-full bg-[#9ACD32] text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid={`mark-paid-${c.commission_id}`}>
                        {t("admin.mark_paid")}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
